import { route } from 'preact-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { RichText } from '../../components/ui/RichText.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useFetch } from '../../hooks/useFetch.js';
import type { LegalDocument, LegalDocumentKind } from '../../services/legalDocuments.js';
import { LEGAL_DOCUMENT_TITLES } from '../../services/legalDocuments.js';
import { mytDate } from '../../utils/mytTime.js';

interface LegalDocumentPageProps {
  kind: LegalDocumentKind;
  path?: string;
}

/**
 * 160 (US2): a legal document, as published by the operator.
 *
 * Public — registered outside `AuthGuard`, keeping feature 153's reasoning for `/terms`: a person
 * asked to agree to a document should be able to read it without an account, and a link that
 * bounces to sign-in is a link most people never follow.
 *
 * 🔴 A failed read says so and offers a retry. It must NEVER fall through to an empty page: "we
 * could not reach the platform" and "there are no terms" are different sentences, and only one of
 * them is honest. That is the 064 defect this repo has already paid for once.
 *
 * 208 (#308): back is the shared `PageHeader` — real history first, `fallback="/"` second. This
 * used to be an inline copy of that exact logic (Constitution VI: one definition). History-first
 * is what keeps feature 153 FR-004 true: a customer reaching this from the middle of a half-filled
 * booking form returns to it with contents intact, since the fallback branch is never reached when
 * real history exists.
 */
export function LegalDocumentPage({ kind }: LegalDocumentPageProps) {
  const read = useFetch<LegalDocument>(`/legal-documents/${kind}`);
  const title = LEGAL_DOCUMENT_TITLES[kind];

  return (
    <div className="page-fill bg-bg px-4 pt-6 pb-10">
      <PageHeader title={title} fallback="/" />

      {read.status === 'loading' || read.status === 'idle' ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : read.status === 'ok' ? (
        <>
          {/* The version is on the page so a customer and a support agent can be certain they are
              reading the same text — a booking records this same string. */}
          <p className="text-xs text-gray-500 mb-6">
            Version {read.data.version} · in effect since {mytDate(read.data.published_at)}
          </p>
          <RichText html={read.data.body_html} />
        </>
      ) : (
        <ResultState
          state={read}
          onRetry={read.retry}
          goneMessage={`${title} is not available.`}
          onwardLabel="Back to search"
          onOnward={() => route('/search')}
        />
      )}
    </div>
  );
}

/** Routed components — `preact-router` passes `path`, and the kind is fixed per address. */
export function TermsOfServicePage(_props: { path?: string }) {
  return <LegalDocumentPage kind="terms_of_service" />;
}

export function PrivacyPolicyPage(_props: { path?: string }) {
  return <LegalDocumentPage kind="privacy_policy" />;
}
